import React from 'react';
import { FormattedMessage } from 'react-intl';

import { useDispatch, useSelector } from 'hooks';
import { useLazyGetLatestMessageQuery } from 'modules/kiosk/service';

import PageBaseProps from 'types/PageBase';

import { logout } from 'modules/auth/actions';
import { openKeysSafe } from 'modules/kiosk/actions';

import {
  getKioskId,
  getIsCloseSuccess,
  getSlotIdBySelfServiceId,
  hasError as hasErrorSelector,
} from 'modules/kiosk/selectors';
import { getKioskImages } from 'modules/dealers/selectors';
import { getSelectedSelfService } from 'modules/selfServices/selectors';

import Modal from 'components/ui/Modal';
import Button, { ButtonType } from 'components/ui/Button';
import KeysError from 'components/Keys/PartteamKey/KeysError';

import useShareboxKeyTranslations from './useShareboxKeyTranslations';
import ProcessingStepModal, { ProcessingStep } from '../../ui/ProcessingStepModal';
import { KEYCHAIN_INSTRUCTIONS_STEP } from '../../KioskLocationId/KeychainInstructions';
import { SelfServiceType } from 'modules/selfServices/types/SelfService';

const STEPS = {
  [ProcessingStep.KEYCHAIN_INSTRUCTIONS]: KEYCHAIN_INSTRUCTIONS_STEP,
};

const ShareboxKey: React.FC<PageBaseProps> = ({ onNext }) => {
  const dispatch = useDispatch();

  const [getLatestMessage] = useLazyGetLatestMessageQuery();

  const kioskId = useSelector(getKioskId);
  const hasError = useSelector(hasErrorSelector);
  const kioskImages = useSelector(getKioskImages);
  const isDoorClosed = useSelector(getIsCloseSuccess);
  const selfService = useSelector(getSelectedSelfService);
  const slotId = useSelector((state) => getSlotIdBySelfServiceId(state, selfService.id));

  const [isKeychainInstructionModalOpen, setIsKeychainInstructionModalOpen] = React.useState(false);

  const handleLogout = React.useCallback(() => {
    dispatch(logout());
  }, [dispatch]);

  const handleOpenDoor = React.useCallback(() => {
    setIsKeychainInstructionModalOpen(false);
    dispatch(openKeysSafe({ selfServiceId: selfService.id, selfServiceType: selfService.type }));
  }, [dispatch, selfService.id, selfService.type]);

  const handleVisibilityChange = React.useCallback(() => {
    if (!document.hidden) {
      getLatestMessage({ kioskId, slotId, processId: selfService.id });
    }
  }, [getLatestMessage, kioskId, selfService.id, slotId]);

  React.useEffect(() => {
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [handleVisibilityChange]);

  React.useEffect(() => {
    if (selfService.type !== SelfServiceType.CHECK_OUT) {
      setIsKeychainInstructionModalOpen(true);
    } else {
      handleOpenDoor();
    }
    // We only want to run this effect when the modal is opened
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [handleOpenDoor]);

  React.useEffect(() => {
    if (isDoorClosed) {
      onNext();
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [isDoorClosed]);

  const description = useShareboxKeyTranslations();

  return (
    <>
      <div className="main-content max-h-svh overflow-hidden">
        <div className="h-full flex grow flex-col items-center">
          <div className="content pb-10">
            <p className="text-center mt-5 text-low">
              <FormattedMessage {...description} />
            </p>
          </div>
          <div className="grow flex items-end justify-center w-full ">
            <img
              alt="Sharebox kiosk"
              src={kioskImages?.transparentImage}
              className=" translate-y-4 landscape:max-w-64 lg:landscape:max-w-full"
            />
          </div>
        </div>
      </div>
      {hasError && (
        <Modal outsideClickClose={false} className="modal-large" open>
          <KeysError
            onOpen={handleOpenDoor}
            onClose={handleOpenDoor}
            className="[&>img]:hidden gap-0 [&>#keys-modal-actions]:flex-col"
          >
            <Button testId="logOut" className="w-72" type={ButtonType.SECONDARY} onClick={handleLogout}>
              <FormattedMessage id="header.title" defaultMessage="Log out" />
            </Button>
          </KeysError>
        </Modal>
      )}
      <ProcessingStepModal
        open={isKeychainInstructionModalOpen}
        config={STEPS}
        step={ProcessingStep.KEYCHAIN_INSTRUCTIONS}
        onClick={handleOpenDoor}
      />
    </>
  );
};

export default ShareboxKey;
